import OSS from 'ali-oss';
import {
  getOssObjectUrl,
  getOssPolicy,
  type OssPolicyRecord,
} from '@/services/modules/public';
import { readFileAsDataUrl } from './file';

export interface OssUploadResult {
  url: string;
  rawUrl: string;
  objectKey: string;
}

export interface OssUploadOptions {
  dir?: string;
}

const DEFAULT_OSS_REGION = 'oss-cn-hangzhou';
const DEFAULT_OSS_DIR = 'shop/';

const normalizeDir = (dir?: string) => {
  const nextDir = String(dir || DEFAULT_OSS_DIR)
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/');

  if (!nextDir) {
    return DEFAULT_OSS_DIR;
  }

  return nextDir.endsWith('/') ? nextDir : `${nextDir}/`;
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const resolveUploadDir = (policyDir?: string, customDir?: string) => {
  const normalizedPolicyDir = normalizeDir(policyDir || DEFAULT_OSS_DIR);
  const normalizedCustomDir = String(customDir || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .replace(/\/+$/, '');

  if (!normalizedCustomDir) {
    return normalizedPolicyDir;
  }

  const policyDirWithoutSlash = trimTrailingSlash(normalizedPolicyDir);
  const policyRootSegment =
    normalizedPolicyDir.split('/').filter(Boolean)[0] || '';

  let relativeDir = normalizedCustomDir;

  if (relativeDir === policyDirWithoutSlash) {
    relativeDir = '';
  } else if (relativeDir.startsWith(`${policyDirWithoutSlash}/`)) {
    relativeDir = relativeDir.slice(policyDirWithoutSlash.length + 1);
  } else if (policyRootSegment) {
    if (relativeDir === policyRootSegment) {
      relativeDir = '';
    } else if (relativeDir.startsWith(`${policyRootSegment}/`)) {
      relativeDir = relativeDir.slice(policyRootSegment.length + 1);
    }
  }

  if (!relativeDir) {
    return normalizedPolicyDir;
  }

  return normalizeDir(`${normalizedPolicyDir}${relativeDir}`);
};

const buildSafeFileName = (fileName: string) => {
  const nextFileName = String(fileName || '')
    .trim()
    .replace(/[^a-zA-Z0-9_.-]/g, '');

  return nextFileName || `file_${Date.now()}`;
};

const getOssRegion = (policy: OssPolicyRecord) =>
  String(policy.region || DEFAULT_OSS_REGION).trim() || DEFAULT_OSS_REGION;

const getLatestPolicy = async () => getOssPolicy();

const createOssClient = (policy: OssPolicyRecord) => {
  const region = getOssRegion(policy);

  // 这里沿用旧项目的 STS 直传模式，后续只要后端保持同一返回结构即可复用。
  return new OSS({
    region,
    accessKeyId: policy.access_key_id,
    accessKeySecret: policy.access_key_secret,
    stsToken: policy.security_token,
    bucket: policy.bucket,
    secure: true,
    refreshSTSToken: async () => {
      const nextPolicy = await getLatestPolicy();
      return {
        accessKeyId: nextPolicy.access_key_id,
        accessKeySecret: nextPolicy.access_key_secret,
        stsToken: nextPolicy.security_token,
      };
    },
  });
};

const buildRawOssUrl = (
  policy: OssPolicyRecord,
  objectKey: string,
  originUrl?: string,
) => {
  if (originUrl) {
    return originUrl;
  }

  return `https://${policy.bucket}.${getOssRegion(policy)}.aliyuncs.com/${objectKey}`;
};

export const uploadFileToOss = async (
  file: File,
  options?: OssUploadOptions,
): Promise<OssUploadResult> => {
  const policy = await getLatestPolicy();
  const resolvedDir = resolveUploadDir(policy.dir, options?.dir);

  if (policy.mock) {
    const dataUrl = await readFileAsDataUrl(file);
    const objectKey = `${resolvedDir}${buildSafeFileName(file.name)}`;
    return {
      url: dataUrl,
      rawUrl: dataUrl,
      objectKey,
    };
  }

  if (
    !policy.bucket ||
    !policy.access_key_id ||
    !policy.access_key_secret ||
    !policy.security_token
  ) {
    throw new Error('OSS 配置不完整，请检查后端返回');
  }

  const objectKey = `${resolvedDir}${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}_${buildSafeFileName(file.name)}`;
  const client = createOssClient(policy);
  const uploadResult = await client.put(objectKey, file);
  const rawUrl = buildRawOssUrl(policy, objectKey, uploadResult.url);

  try {
    const finalUrl = await getOssObjectUrl(rawUrl);
    return {
      url: finalUrl.url || rawUrl,
      rawUrl,
      objectKey,
    };
  } catch (_error) {
    // 取最终访问地址失败时，至少保留 OSS 原始地址，避免上传成功却无法回填。
    return {
      url: rawUrl,
      rawUrl,
      objectKey,
    };
  }
};
